import { SEO } from '@/components/SEO'
import { ContactForm } from '@/components/ContactForm'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useDirectionalReveal } from '@/hooks'
import { Mail, Linkedin, MapPin } from 'lucide-react'

const contactInfo = [
  {
    icon: Mail,
    label: 'Email',
    value: 'garrett@garrettjohnlaw.com',
    href: 'mailto:garrett@garrettjohnlaw.com',
  },
  {
    icon: Linkedin,
    label: 'LinkedIn',
    value: 'garrettjohnlaw',
    href: 'https://linkedin.com/in/garrettjohnlaw',
  },
  {
    icon: MapPin,
    label: 'Location',
    value: 'Los Angeles, CA',
    href: null,
  },
]

export function Contact() {
  // Hero entrance
  const heroRef = useLuxeHero<HTMLElement>(0.3)

  // Contact info from left
  const infoRef = useDirectionalReveal<HTMLDivElement>('.info-card', 'left', {
    stagger: 0.1,
  })

  // Form from right
  const formRef = useDirectionalReveal<HTMLDivElement>('.reveal', 'right', {
    blur: 8,
  })

  return (
    <>
      <SEO
        title="Contact"
        description="Get in touch with Garrett John Law"
        url="https://software.garrettjohnlaw.com/contact"
      />

      {/* Hero Section */}
      <section ref={heroRef} className="relative overflow-hidden px-4 py-16 md:py-24">
        <div className="bg-gradient-radial-cyber absolute inset-0 opacity-30" />
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <p className="text-primary mb-4 font-mono text-lg font-medium tracking-wide">&lt;contact /&gt;</p>
          <h1 className="text-fluid-h1 text-foreground mb-4">Get In Touch</h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            Have a project in mind? Let's discuss how I can help.
          </p>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Contact Content */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-8 lg:grid-cols-5 lg:gap-12">
            {/* Contact Info */}
            <div ref={infoRef} className="space-y-4 lg:col-span-2">
              <h2 className="text-foreground mb-6 text-xl font-semibold">Contact Information</h2>

              {contactInfo.map((info) => (
                <GlassCard key={info.label} hover className="info-card py-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-primary/10 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg">
                      <info.icon className="text-primary h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">{info.label}</p>
                      {info.href ? (
                        <a
                          href={info.href}
                          target={info.href.startsWith('http') ? '_blank' : undefined}
                          rel={info.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-foreground hover:text-primary font-medium transition-colors"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-foreground font-medium">{info.value}</p>
                      )}
                    </div>
                  </div>
                </GlassCard>
              ))}

              <div className="pt-6">
                <GlassCard accent className="info-card">
                  <p className="text-muted-foreground text-sm">
                    I typically respond within <span className="text-primary font-medium">24 hours</span>. For urgent
                    inquiries, please reach out via LinkedIn.
                  </p>
                </GlassCard>
              </div>
            </div>

            {/* Contact Form */}
            <div ref={formRef} className="lg:col-span-3">
              <div className="reveal">
                <GlassCard accent className="overflow-hidden p-0">
                  <ContactForm header="" body="" />
                </GlassCard>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
